import { useMemo, useState, useEffect } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Button from "../../../components/common/Button/Button";
import Modal from "../../../components/common/Modal/Modal";
import { FiDownload } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import GroupedBarChartGraph from "../../../components/common/Charts/GroupedBarChartGraph";
import Table from "../../../components/common/Table/Table";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import ExcluirRaeeZeroWasteToggle from "../../../components/common/Inputs/ExcluirRaeeZeroWasteToggle/ExcluirRaeeZeroWasteToggle";
import { createFilterState } from "../../../lib/reportNew/filter-stateNew";
import {
    useExcelExport,
    buildInformeExportFilters,
    codsFilaDe,
} from "../useExcelExport";
import {
    INDICADORES,
    QUICK_FILTERS_INDICADOR,
    mapaPaises,
    mapaTiposObra,
    normalizarFilas,
    filasVisibles,
} from "../indicadores/definiciones";
import { getApiInstance } from "../../../utils/api";
import { useZone } from "../../../context/ZoneContext";
import { useMoneda } from "../../../context/MonedaContext";
import { simboloMoneda } from "../../../utils/moneda";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./AhorroSegregacion.scss";

const INDICADOR = INDICADORES.ahorroSegregacion;

const COLOR_SEGREGADO = "#2563eb";
const COLOR_MEZCLA = "#dc2626";
const COLOR_AHORRO = "#10b981";

const SERIES = [
    {
        key: "costeSegregado",
        name: "Coste segregado extrapolado",
        color: COLOR_SEGREGADO,
    },
    {
        key: "costeMezcla",
        name: "Coste si todo fuera RCD mezcla",
        color: COLOR_MEZCLA,
    },
    {
        key: "ahorro",
        name: "Ahorro por segregación",
        color: COLOR_AHORRO,
    },
];

const Y_PASO = 2000000; // 2M

// Mezclas, los mismos códigos que en Segregación Total: no cuentan como residuo
// segregado y de ellas sale el €/t de referencia.
const CODIGOS_MEZCLA = ["170904", "200301", "200307"];

const formatNumero = (v, decimals = 2) =>
    new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: decimals,
    }).format(Number(v) || 0);

const columnasTablaCostes = (moneda = "€") => [
    { key: "anio", label: "Año", visible: true, sortable: true, type: "number" },
    { key: "codigoLER", label: "Código LER", visible: true, sortable: true },
    { key: "residuo", label: "Residuo", visible: true, sortable: true },
    {
        key: "tTotales",
        label: "t totales",
        visible: true,
        sortable: true,
        type: "number",
        align: "right",
        render: (row) => formatNumero(row.tTotales),
    },
    {
        key: "kgTotales",
        label: "kg totales",
        visible: true,
        sortable: true,
        type: "number",
        align: "right",
        render: (row) => formatNumero(row.kgTotales),
    },
    {
        key: "tConCoste",
        label: "t con coste",
        visible: true,
        sortable: true,
        type: "number",
        align: "right",
        render: (row) => formatNumero(row.tConCoste),
    },
    {
        key: "kgConCoste",
        label: "kg con coste",
        visible: true,
        sortable: true,
        type: "number",
        align: "right",
        render: (row) => formatNumero(row.kgConCoste),
    },
    {
        key: "eurT",
        label: `${moneda}/t`,
        visible: true,
        sortable: true,
        type: "number",
        align: "right",
        render: (row) => formatEuros(row.eurT, moneda),
    },
    {
        key: "eurKg",
        label: `${moneda}/kg`,
        visible: true,
        sortable: true,
        type: "number",
        align: "right",
        render: (row) => formatEuros(row.eurKg, moneda),
    },
    {
        key: "costeExtrapolado",
        label: `Coste extrapolado (${moneda})`,
        visible: true,
        sortable: true,
        type: "number",
        align: "right",
        render: (row) => formatEuros(row.costeExtrapolado, moneda),
    },
];

const formatEuros = (v, moneda = "€") => `${formatNumero(v)} ${moneda}`;

export default function AhorroSegregacion() {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [informeData, setInformeData] = useState(null);

    const { selectedZone } = useZone();
    const { moneda } = useMoneda();
    const simbolo = simboloMoneda(moneda);

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
        useExcelExport({
            onError: setError,
            resumenUrl: "api/Informe/CreateReportExcelAhorroSegregacionResumen",
        });

    const { filterFields } = INDICADOR;

    const [filterState, setFilterState] = useState(() =>
        createFilterState(QUICK_FILTERS_INDICADOR)
    );

    const [paisesArr, setPaisesArr] = useState([]);
    const [tiposObraArr, setTiposObraArr] = useState([]);

    /* Se vuelve a pedir al cambiar de moneda: la conversión la hace el backend. */
    useEffect(() => {
        if (!moneda) return;

        const fetchInforme = async () => {
            setLoading(true);
            setError(null);
            try {
                const api = getApiInstance();
                const response = await api.get(INDICADOR.datosUrl(moneda));
                const data = response?.data ?? {};
                setInformeData(
                    Array.isArray(data?.residuos) && data.residuos.length
                        ? data
                        : null
                );
            } catch (err) {
                console.error(err);
                setError("No se ha podido cargar el informe.");
            } finally {
                setLoading(false);
            }
        };
        fetchInforme();
    }, [moneda]);

    useEffect(() => {
        const api = getApiInstance();

        const fetchPaises = async () => {
            try {
                const response = await api.get(`/api/Paises/GetPaises`);
                setPaisesArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchPaises();

        const fetchTiposObra = async () => {
            try {
                const response = await api.get(
                    `/api/ObrasTipo/GetAllObrasTipo`
                );
                setTiposObraArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchTiposObra();
    }, []);

    const mapPaises = useMemo(() => mapaPaises(paisesArr), [paisesArr]);
    const mapTiposObra = useMemo(
        () => mapaTiposObra(tiposObraArr),
        [tiposObraArr]
    );
    const reversePaises = useMemo(
        () =>
            Object.fromEntries(
                paisesArr.map((p) => [String(p.pais), String(p.codPais)])
            ),
        [paisesArr]
    );
    const reverseTiposObra = useMemo(
        () =>
            Object.fromEntries(
                tiposObraArr.map((t) => [
                    String(t.descripcion),
                    String(t.codTipoObra),
                ])
            ),
        [tiposObraArr]
    );

    const residuosArr = useMemo(
        () => INDICADOR.filasDe(informeData),
        [informeData]
    );

    const residuosNormalizados = useMemo(
        () => normalizarFilas(residuosArr, mapPaises, mapTiposObra),
        [residuosArr, mapPaises, mapTiposObra]
    );

    // Activado por defecto: el Excel de referencia del indicador se hace sin RAEE / Zero Waste.
    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(true);

    const residuosFiltrados = useMemo(
        () =>
            filasVisibles(
                INDICADOR,
                residuosNormalizados,
                filterState,
                excluirRaeeZeroWaste
            ),
        [residuosNormalizados, filterState, excluirRaeeZeroWaste]
    );

    const exportFilters = useMemo(() => {
        const quick = filterState.quick || {};
        const anios = Array.isArray(quick.anio) ? quick.anio : [];
        const paises = Array.isArray(quick.pais) ? quick.pais : [];
        const tipos = Array.isArray(quick.tipoObra) ? quick.tipoObra : [];
        return {
            ...buildInformeExportFilters({
                selectedAnio: anios.length ? anios[0] : "all",
                selectedPais: paises.length
                    ? reversePaises[paises[0]] ?? paises[0]
                    : "all",
                selectedTipoObra: tipos.map((t) => reverseTiposObra[t] ?? t),
                selectedZonas: quick.codZona,
            }),
            /* Lo que manda de verdad: las filas que se están viendo. Los filtros
               de arriba se quedan por compatibilidad. */
            codsFila: codsFilaDe(residuosFiltrados),
        };
    }, [filterState, reversePaises, reverseTiposObra, residuosFiltrados]);

    const informeCalculado = useMemo(() => {
        const tarifas = Array.isArray(informeData?.tarifas)
            ? informeData.tarifas
            : [];
        const tarifasDetalles = Array.isArray(informeData?.tarifasDetalles)
            ? informeData.tarifasDetalles
            : [];

        const filtrados = residuosFiltrados;

        const categoriaCodLER = (cod) => (cod || "").substring(0, 2);

        const getTarifaGestion = (codObra, codResiduo) => {
            const obraTarifa = tarifas.find((t) => t.codObra === codObra);
            if (!obraTarifa) return null;
            const detalle = tarifasDetalles.find(
                (d) =>
                    d.codTarifa === obraTarifa.codTarifa &&
                    d.codResiduo === codResiduo
            );
            if (!detalle) return null;
            if (detalle.gestion !== null && detalle.gestion > 0)
                return detalle.gestion;
            if (detalle.deshecho !== null && detalle.deshecho > 0)
                return detalle.deshecho;
            return null;
        };

        const porAnio = {};
        filtrados.forEach((r) => {
            if (r.peligroso !== false) return;
            // Cada línea cuenta en el año de fin de su obra, el mismo por el que
            // filtra "Año": así el informe de un año no pinta columnas de otros.
            const anio = Number(r.anio) || null;
            if (!anio) return;
            const codRes = r.codResiduo;
            const peso = (Number(r.peso) || 0) / 1000;
            if (peso <= 0) return;

            if (!porAnio[anio]) porAnio[anio] = {};
            if (!porAnio[anio][codRes]) {
                porAnio[anio][codRes] = {
                    codResiduo: codRes,
                    codigo: r.codigo ? String(r.codigo).trim() : "",
                    nombre: r.nombre || "",
                    tTotales: 0,
                    sumaCoste: 0,
                    tConCoste: 0,
                    costePorTon: null,
                    costePorTonReal: null,
                    esExtrapolado: false,
                };
            }

            const ler = porAnio[anio][codRes];
            ler.tTotales += peso;

            // El coste real de la línea son todos sus conceptos (transporte,
            // gestión, colocación, alquiler y documento): muchas veces se graba el
            // total en uno solo sin desglosar. La tarifa solo se usa si la línea no
            // tiene ninguno.
            // El abono de chatarra es un dato económico más del residuo (se graba
            // en negativo, es un ingreso). Si un residuo solo tiene abono, cuenta
            // como residuo con coste y deja de salir en amarillo con €/t = 0.
            const costeTotal =
                r.costeTotal !== null && r.costeTotal !== undefined
                    ? parseFloat(r.costeTotal)
                    : 0;
            const abono =
                r.abonoChatarra !== null && r.abonoChatarra !== undefined
                    ? parseFloat(r.abonoChatarra)
                    : 0;
            let costeReal = 0;
            let tieneCoste = false;

            if (costeTotal !== 0) {
                costeReal = costeTotal;
                tieneCoste = true;
            } else {
                // La tarifa no tiene fecha propia: se convierte con el factor
                // del residuo al que se le aplica.
                const precioTarifa = getTarifaGestion(r.codObra, codRes);
                const precioTon =
                    precioTarifa === null
                        ? null
                        : precioTarifa * (Number(r.factorMoneda) || 1);
                if (precioTon !== null && precioTon > 0) {
                    costeReal = precioTon * peso;
                    tieneCoste = true;
                }
            }
            if (abono !== 0) {
                costeReal += abono;
                tieneCoste = true;
            }
            if (tieneCoste) {
                ler.sumaCoste += costeReal;
                ler.tConCoste += peso;
            }
        });

        Object.keys(porAnio).forEach((anio) => {
            Object.keys(porAnio[anio]).forEach((cr) => {
                const ler = porAnio[anio][cr];
                const cpt = ler.tConCoste > 0 ? ler.sumaCoste / ler.tConCoste : null;
                ler.costePorTonReal = cpt;
                ler.costePorTon = cpt;
                ler.esExtrapolado = cpt === null;
            });
        });

        Object.keys(porAnio).forEach((anio) => {
            const lerMap = porAnio[anio];
            const anioNum = parseInt(anio, 10);
            Object.keys(lerMap).forEach((cr) => {
                const ler = lerMap[cr];
                if (ler.costePorTonReal !== null) return;

                const cat = categoriaCodLER(ler.codigo);
                const similares = Object.values(lerMap).filter(
                    (l) =>
                        l.costePorTonReal !== null &&
                        l.codResiduo !== ler.codResiduo &&
                        categoriaCodLER(l.codigo) === cat
                );
                if (similares.length > 0) {
                    ler.costePorTon =
                        similares.reduce(
                            (a, l) => a + l.costePorTonReal,
                            0
                        ) / similares.length;
                    return;
                }

                const otrosCoste = [];
                Object.keys(porAnio).forEach((otroAnio) => {
                    if (parseInt(otroAnio, 10) === anioNum) return;
                    const lerOtro = porAnio[otroAnio][cr];
                    if (
                        lerOtro &&
                        lerOtro.costePorTonReal !== null &&
                        lerOtro.costePorTonReal !== undefined
                    )
                        otrosCoste.push(lerOtro.costePorTonReal);
                });
                if (otrosCoste.length > 0) {
                    ler.costePorTon =
                        otrosCoste.reduce((a, b) => a + b, 0) /
                        otrosCoste.length;
                    return;
                }

                ler.costePorTon = 0;
            });
        });

        // €/t de la mezcla en un año: coste real de los códigos de mezcla (sin
        // estimar nada) entre sus toneladas con coste. Igual que en el Excel de
        // referencia: no se le inventa precio a lo que no tiene coste registrado.
        const costeTonMezclaAnio = (anio) => {
            let coste = 0;
            let tConCoste = 0;
            Object.values(porAnio[anio]).forEach((l) => {
                if (!CODIGOS_MEZCLA.includes(l.codigo)) return;
                coste += l.sumaCoste;
                tConCoste += l.tConCoste;
            });
            return tConCoste > 0 ? coste / tConCoste : null;
        };

        const costesRcd = Object.keys(porAnio)
            .map(costeTonMezclaAnio)
            .filter((c) => c !== null);
        const costeTonMezclaGlobal =
            costesRcd.length > 0
                ? costesRcd.reduce((a, b) => a + b, 0) / costesRcd.length
                : 0;

        const filas = [];
        const ahorrosPorAnio = {};

        Object.keys(porAnio)
            .sort()
            .forEach((anio) => {
                const lerMap = porAnio[anio];
                const anioNum = parseInt(anio, 10);

                // Coste real segregado y coste real total: solo lo que hay grabado, sin
                // estimar precio para las toneladas sin ningún coste (igual que el Excel
                // de referencia). La estimación por capítulo/año sigue existiendo solo
                // para el detalle por LER de la tabla de abajo.
                let costeSegregadoAnio = 0;
                let costeTodosAnio = 0;
                let tTodos = 0;

                Object.keys(lerMap).forEach((cr) => {
                    const ler = lerMap[cr];
                    const cpt =
                        ler.costePorTon !== null && !isNaN(ler.costePorTon)
                            ? ler.costePorTon
                            : 0;
                    const costeExtrap = cpt * ler.tTotales;
                    ler.costeExtrapolado = costeExtrap;

                    costeTodosAnio += ler.sumaCoste;
                    tTodos += ler.tTotales;
                    if (!CODIGOS_MEZCLA.includes(ler.codigo))
                        costeSegregadoAnio += ler.sumaCoste;

                    filas.push({
                        anio: anioNum,
                        codigo: ler.codigo,
                        nombre: ler.nombre,
                        tTotales: ler.tTotales,
                        tConCoste: ler.tConCoste,
                        costePorTon: cpt,
                        costeExtrapolado: costeExtrap,
                        esExtrapolado: ler.esExtrapolado,
                    });
                });

                const costeTonMezcla =
                    costeTonMezclaAnio(anio) ?? costeTonMezclaGlobal;

                // "Coste si todo fuera mezcla" se calcula sobre TODAS las toneladas del año
                // (mezcla incluida), no solo las segregadas: es lo que costaría el total del
                // residuo si se hubiera gestionado entero como mezcla.
                ahorrosPorAnio[anioNum] = {
                    costeSegregado: costeSegregadoAnio,
                    costeSiNoSegregado: costeTonMezcla * tTodos,
                    // El ahorro se calcula con todos los residuos, mezcla incluida: lo
                    // que costaría todo como mezcla menos lo que ha costado todo.
                    ahorro: costeTonMezcla * tTodos - costeTodosAnio,
                };
            });

        const aniosLista = Object.keys(ahorrosPorAnio)
            .map(Number)
            .sort((a, b) => a - b);

        const datosGrafico = aniosLista.map((a) => ({
            name: String(a),
            costeSegregado: ahorrosPorAnio[a].costeSegregado,
            costeMezcla: ahorrosPorAnio[a].costeSiNoSegregado,
            ahorro: ahorrosPorAnio[a].ahorro,
        }));

        const datosTabla = filas
            .slice()
            .sort(
                (a, b) =>
                    a.anio - b.anio ||
                    (a.codigo || "").localeCompare(b.codigo || "")
            )
            .map((f, i) => ({
                id: `${f.anio}|${f.codigo}|${i}`,
                anio: f.anio,
                codigoLER: f.codigo,
                residuo: f.nombre,
                tTotales: f.tTotales,
                tConCoste: f.tConCoste,
                eurT: f.costePorTon,
                costeExtrapolado: f.costeExtrapolado,
                esEstimado: f.esExtrapolado,
                kgTotales: f.tTotales * 1000,
                kgConCoste: f.tConCoste * 1000,
                eurKg: f.costePorTon / 1000,
            }));

        const cards = {
            costeSegregado: aniosLista.reduce(
                (s, a) => s + ahorrosPorAnio[a].costeSegregado,
                0
            ),
            costeMezcla: aniosLista.reduce(
                (s, a) => s + ahorrosPorAnio[a].costeSiNoSegregado,
                0
            ),
            ahorro: aniosLista.reduce(
                (s, a) => s + ahorrosPorAnio[a].ahorro,
                0
            ),
        };

        return { datosGrafico, datosTabla, cards };
    }, [informeData, residuosFiltrados]);

    const yFormatter = (v) => (v === 0 ? "0" : `${v / 1000000}M`);

    const { datosGrafico, datosTabla, cards } = informeCalculado;

    const yTicks = useMemo(() => {
        const maxValor = datosGrafico.reduce(
            (max, d) =>
                Math.max(max, d.costeSegregado, d.costeMezcla, d.ahorro),
            0
        );
        const minValor = datosGrafico.reduce(
            (min, d) =>
                Math.min(min, d.costeSegregado, d.costeMezcla, d.ahorro),
            0
        );

        const techo = Math.ceil(maxValor / Y_PASO) * Y_PASO;
        const piso = Math.min(
            -Y_PASO,
            Math.floor(minValor / Y_PASO) * Y_PASO
        );

        const ticks = [];
        for (let v = piso; v <= techo; v += Y_PASO) ticks.push(v);
        return ticks;
    }, [datosGrafico]);

    const renderFilters = () => (
        <div className="filters-section-common">
            <FilterBar
                rows={residuosNormalizados}
                state={filterState}
                onChange={setFilterState}
                resultCount={residuosFiltrados.length}
                totalCount={residuosNormalizados.length}
                fields={filterFields}
                excludeFields={INDICADOR.excludeFields}
                headerExtra={
                    <ExcluirRaeeZeroWasteToggle
                        activo={excluirRaeeZeroWaste}
                        onChange={setExcluirRaeeZeroWaste}
                    />
                }
            />
        </div>
    );

    return (
        <div className="ahorro-segregacion-page">
            <PageLayout
                title="Informe de Ahorro por Segregación"
                subtitle="Ahorro económico derivado de la segregación de residuos"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <div className="tab-content-with-filters">
                    {renderFilters()}

                    {loading ? (
                        <div
                            className="informe-page-skeleton"
                            aria-hidden="true"
                        >
                            <div className="informe-page-skeleton__cards">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="informe-page-skeleton__card"
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="ahorro-cards">
                        <div className="ahorro-card">
                            <h4 className="ahorro-card__title">
                                Coste real segregado extrapolado
                            </h4>
                            <div className="ahorro-card__amount ahorro-card__amount--blue">
                                {formatEuros(cards.costeSegregado, simbolo)}
                            </div>
                            <p className="ahorro-card__subtitle">
                                Gestionando cada residuo según su tipo
                            </p>
                        </div>

                        <div className="ahorro-card">
                            <h4 className="ahorro-card__title">
                                Coste si todo fuera mezcla (170904, 200301, 200307)
                            </h4>
                            <div className="ahorro-card__amount ahorro-card__amount--red">
                                {formatEuros(cards.costeMezcla, simbolo)}
                            </div>
                            <p className="ahorro-card__subtitle">
                                Aplicando €/t de la mezcla a todos los residuos
                                distintos
                            </p>
                        </div>

                        <div className="ahorro-card">
                            <h4 className="ahorro-card__title">
                                Ahorro por segregación
                            </h4>
                            <div className="ahorro-card__amount ahorro-card__amount--green">
                                {formatEuros(cards.ahorro, simbolo)}
                            </div>
                            <p className="ahorro-card__subtitle">
                                € ahorrados gracias a la segregación
                            </p>
                        </div>
                    </div>
                    )}

                    <div className="cantidades-generadas-content">
                        <div className="graficas-card">
                            <div className="charts-section">
                                <div className="chart-container">
                                    <div className="chart-header">
                                        <h3>
                                            Ahorro por segregación por año
                                        </h3>
                                    </div>
                                    <div className="chart-wrapper">
                                        {loading ? (
                                            <div
                                                className="informe-chart-skeleton informe-chart-skeleton--bar"
                                                aria-hidden="true"
                                            >
                                                <div className="informe-chart-skeleton__bars">
                                                    {[
                                                        70, 82, 62, 74, 50,
                                                        60, 42,
                                                    ].map((h, i) => (
                                                        <div
                                                            key={i}
                                                            className="informe-chart-skeleton__bar"
                                                            style={{
                                                                height: `${h}%`,
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <GroupedBarChartGraph
                                                data={datosGrafico}
                                                series={SERIES}
                                                yTicks={yTicks}
                                                yFormatter={yFormatter}
                                                simbolo={simbolo}
                                            />
                                        )}
                                    </div>
                                    <div className="ahorro-legend">
                                        <div className="ahorro-legend__item">
                                            <span
                                                className="ahorro-legend__dot"
                                                style={{
                                                    background: COLOR_SEGREGADO,
                                                }}
                                            />
                                            <span className="ahorro-legend__label">
                                                Coste segregado extrapolado
                                            </span>
                                        </div>
                                        <div className="ahorro-legend__item">
                                            <span
                                                className="ahorro-legend__dot"
                                                style={{
                                                    background: COLOR_MEZCLA,
                                                }}
                                            />
                                            <span className="ahorro-legend__label">
                                                Coste si todo fuera RCD mezcla
                                            </span>
                                        </div>
                                        <div className="ahorro-legend__item">
                                            <span
                                                className="ahorro-legend__dot"
                                                style={{
                                                    background: COLOR_AHORRO,
                                                }}
                                            />
                                            <span className="ahorro-legend__label">
                                                Ahorro por segregación
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="cantidades-generadas-content">
                        <div className="graficas-card">
                            <div className="graficas-card__header">
                                <h3 className="graficas-card__title">
                                    Tabla costes extrapolados
                                </h3>
                                <p className="graficas-card__note">
                                    Las filas en{" "}
                                    <mark className="graficas-card__highlight">
                                        amarillo
                                    </mark>{" "}
                                    indican que no se dispone de coste directo
                                    para ese código LER en ese año y se ha
                                    estimado utilizando el coste promedio de un
                                    LER de la misma categoría.
                                </p>
                            </div>
                            <Table
                                columns={columnasTablaCostes(simbolo)}
                                dataTable={datosTabla}
                                keyProp="id"
                                sortProp="anio"
                                showCheckboxes={false}
                                loading={loading}
                                hideFilterBar
                                rowsPerPage={10}
                                getRowClassName={(row) =>
                                    row.esEstimado
                                        ? "table-row-estimated"
                                        : ""
                                }
                                noResultsMessage="No hay datos de costes extrapolados."
                                elementName="coste extrapolado"
                            />
                        </div>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <Modal
                    isOpen={isModalExcelOpen}
                    onClose={closeModal}
                    title="Exportar informe a Excel"
                    actions={[
                        <Button
                            key="export-resumen"
                            className="export-excel__btn export-excel__btn--primary"
                            onClick={() =>
                                handleExportToExcel("resumen", exportFilters)
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "resumen"
                                ? "Generando..."
                                : "Informe Ahorro por Segregación"}
                        </Button>,
                        <Button
                            key="export-completo"
                            className="export-excel__btn export-excel__btn--secondary"
                            onClick={() =>
                                handleExportInformeCompleto({ filterState, excluirRaeeZeroWaste })
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "completo"
                                ? "Generando..."
                                : "Informe Completo"}
                        </Button>,
                    ]}
                >
                    <div className="export-excel__actions"></div>
                </Modal>
            </PageLayout>
        </div>
    );
}
